// ADV-1223 / ADR-0055: browser real-user monitoring via the Dash0 OpenTelemetry
// web SDK. Auto-instruments page load, Core Web Vitals, JS errors, and fetch,
// and propagates W3C `traceparent` to the same-origin `adva-api` Worker so
// browser spans stitch end-to-end to the backend traces from ADV-1192.
//
// This runs ALONGSIDE PostHog (see lib/posthog.ts) — PostHog is product
// analytics + session replay; Dash0 is distributed tracing. Both load on the
// frontend. v1 sends RUM to Dash0 only (no fan-out) per the ticket.
import { init, type UrlAttributeRecord } from "@dash0/sdk-web";

// Default Dash0 OTLP ingress (matches the dash-zero-{traces,logs} destinations
// from the ADV-1192 backend pilot). The URL excludes the `/v1/*` suffix.
const DEFAULT_ENDPOINT_URL = "https://ingress.us-west-2.aws.dash0.com";

// Query/fragment parameter names whose VALUES are credentials and must never
// reach telemetry. RUM auto-captures page/navigation URLs, and routes like
// `/accept-invite?token=...&token_hash=...` (apps/web/src/pages/accept-invite.tsx)
// and the OAuth callbacks read secrets straight from the URL — so the raw URL
// would otherwise export the live credential to Dash0 (Codex P1, ADV-1223).
const SENSITIVE_PARAM =
  /^(token|token_hash|access_token|refresh_token|id_token|code|secret|client_secret|api_?key|key|password|pwd|state)$/i;

const REDACTED = "REDACTED";

// Replace sensitive param values inside a query/fragment string (with or
// without its leading `?`/`#`), preserving keys + structure so the URL stays
// useful telemetry. Returns the input unchanged when nothing matched.
function redactParams(qs: string): string {
  if (!qs) return qs;
  const lead = qs[0] === "?" || qs[0] === "#" ? qs[0] : "";
  const params = new URLSearchParams(lead ? qs.slice(1) : qs);
  let changed = false;
  for (const key of [...params.keys()]) {
    if (SENSITIVE_PARAM.test(key)) {
      params.set(key, REDACTED);
      changed = true;
    }
  }
  return changed ? lead + params.toString() : qs;
}

// Dash0 `urlAttributeScrubber`: invoked for every url placed on a signal
// (`url.*` and `page.url.*`). Redact credential-bearing params from the full
// url, the query, and the fragment (Supabase implicit-flow tokens land in
// `#access_token=`). Basic-auth creds in the url are already redacted by the
// SDK before this runs.
export function scrubSensitiveUrlAttributes(
  attr: UrlAttributeRecord,
): UrlAttributeRecord {
  const next: UrlAttributeRecord = { ...attr };

  const full = next["url.full"];
  if (typeof full === "string") {
    try {
      const u = new URL(full);
      let changed = false;
      for (const key of [...u.searchParams.keys()]) {
        if (SENSITIVE_PARAM.test(key)) {
          u.searchParams.set(key, REDACTED);
          changed = true;
        }
      }
      if (u.hash.length > 1) {
        const scrubbedHash = redactParams(u.hash);
        if (scrubbedHash !== u.hash) {
          u.hash = scrubbedHash;
          changed = true;
        }
      }
      if (changed) next["url.full"] = u.toString();
    } catch {
      // Unparseable url — leave it untouched.
    }
  }

  if (typeof next["url.query"] === "string") {
    next["url.query"] = redactParams(next["url.query"]);
  }
  if (typeof next["url.fragment"] === "string") {
    next["url.fragment"] = redactParams(next["url.fragment"]);
  }

  return next;
}

let initialized = false;

// Browser → API is same-origin (`/api/v1`, see lib/api-client.ts), so the OTel
// fetch instrumentation already propagates `traceparent` to it by default.
// Scope an explicit traceparent propagator to the same-origin `/api/` path: a
// behavioral no-op today, future-proof if the API ever moves to its own origin.
function sameOriginApiMatch(): RegExp {
  const origin = window.location.origin.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`^${origin}/api/`);
}

/**
 * Initialize browser RUM. No-ops when `VITE_DASH0_AUTH_TOKEN` is absent (local
 * dev without the ingest token, mirroring `initPostHog`). Idempotent. Telemetry
 * must never break the app, so init failures are logged and swallowed.
 *
 * Call as early as possible — before React renders — so the fetch
 * instrumentation patches `window.fetch` ahead of the first request.
 */
export function initObservability(): void {
  if (initialized) return;

  const authToken = import.meta.env.VITE_DASH0_AUTH_TOKEN as string | undefined;
  if (!authToken) return;

  const url =
    (import.meta.env.VITE_DASH0_ENDPOINT_URL as string | undefined) ||
    DEFAULT_ENDPOINT_URL;
  const dataset = import.meta.env.VITE_DASH0_DATASET as string | undefined;
  const environment = import.meta.env.VITE_DASH0_ENVIRONMENT as
    | string
    | undefined;
  const serviceVersion = import.meta.env.VITE_APP_VERSION as string | undefined;

  try {
    init({
      serviceName: "adva-web",
      // Groups adva-web with the backend adva-* services in Dash0/Honeycomb
      // (ADR-0057 rule #3 — matches the hand-rolled exporters' service.namespace).
      serviceNamespace: "adva",
      endpoint: { url, authToken, ...(dataset ? { dataset } : {}) },
      ...(environment ? { environment } : {}),
      ...(serviceVersion ? { serviceVersion } : {}),
      // All instrumentations (navigation, web-vitals, error, fetch) enabled by
      // omitting `enabledInstrumentations`.
      propagators: [{ type: "traceparent", match: [sameOriginApiMatch()] }],
      // Redact credential-bearing query/fragment params (invite tokens, OAuth
      // codes, …) from auto-captured URLs before they reach Dash0.
      urlAttributeScrubber: scrubSensitiveUrlAttributes,
    });
    initialized = true;
  } catch (err) {
    // Telemetry failures must surface but never throw.
    console.warn("[observability] Dash0 web SDK init failed:", err);
  }
}
